import React from 'react'
import { useTranslation } from 'react-i18next'
import { Input } from '@/components/ui/input'
import { addDays, isDay, REVIEW_LIMITS, type CycleBasicsDraft, type CycleBasicsErrors } from './reviewUtils'

const FieldError = ({ text }: { text?: string }) => (text ? <p role="alert" className="text-xs text-red-600">{text}</p> : null)

/**
 * Title, the period reviewed (the performance being assessed) and the
 * answering window (criterion 2). Used by the Create Space step, the "new
 * cycle" dialog and the editor. The window's end is a suggestion out of its
 * start (PADROES-UI section 12): rewritten only while nobody changed it.
 */
export const CycleBasicsFields = ({ draft, onChange, errors, disabled = false, lockStructure = false, idPrefix = 'cycle' }: {
  draft: CycleBasicsDraft
  onChange: (draft: CycleBasicsDraft) => void
  errors?: CycleBasicsErrors | null
  disabled?: boolean
  /** Open cycle: only the title and the window's end still change. */
  lockStructure?: boolean
  idPrefix?: string
}) => {
  const { t } = useTranslation('performanceReview')
  const suggested = React.useRef(draft.closesOn)
  const set = (patch: Partial<CycleBasicsDraft>) => onChange({ ...draft, ...patch })

  const setOpensOn = (opensOn: string) => {
    const follow = !draft.closesOn || draft.closesOn === suggested.current
    if (follow && isDay(opensOn)) {
      const next = addDays(opensOn, 14)
      suggested.current = next
      set({ opensOn, closesOn: next })
    } else {
      set({ opensOn })
    }
  }

  const day = (key: keyof CycleBasicsDraft, label: string, value: string, change: (v: string) => void, min?: string, locked = false) => (
    <div className="min-w-0 space-y-1.5">
      <label htmlFor={`${idPrefix}-${key}`} className="text-sm font-medium text-gray-700">{label}</label>
      <Input id={`${idPrefix}-${key}`} type="date" value={value} min={min || undefined} disabled={disabled || locked}
        aria-invalid={Boolean(errors?.[key])} onChange={(e) => change(e.target.value)} data-testid={`${idPrefix}-${key}`} />
      <FieldError text={errors?.[key]} />
    </div>
  )

  return (
    <div className="min-w-0 space-y-5">
      <div className="space-y-1.5">
        <label htmlFor={`${idPrefix}-title`} className="text-sm font-medium text-gray-700">
          {t('cycleForm.title')} <span className="text-red-500">*</span>
        </label>
        <Input id={`${idPrefix}-title`} value={draft.title} maxLength={REVIEW_LIMITS.title} disabled={disabled}
          aria-invalid={Boolean(errors?.title)} placeholder={t('cycleForm.titlePlaceholder')}
          onChange={(e) => set({ title: e.target.value })} data-testid={`${idPrefix}-title`} />
        <FieldError text={errors?.title} />
      </div>

      <fieldset className="min-w-0 space-y-2">
        <legend className="text-sm font-semibold text-gray-900">{t('cycleForm.period')}</legend>
        <div className="grid gap-4 sm:grid-cols-2">
          {day('periodStartsOn', t('cycleForm.periodStartsOn'), draft.periodStartsOn, (v) => set({ periodStartsOn: v }), undefined, lockStructure)}
          {day('periodEndsOn', t('cycleForm.periodEndsOn'), draft.periodEndsOn, (v) => set({ periodEndsOn: v }), draft.periodStartsOn, lockStructure)}
        </div>
        <p className="text-xs text-gray-500">{t('cycleForm.periodHint')}</p>
      </fieldset>

      <fieldset className="min-w-0 space-y-2">
        <legend className="text-sm font-semibold text-gray-900">{t('cycleForm.window')}</legend>
        <div className="grid gap-4 sm:grid-cols-2">
          {day('opensOn', t('cycleForm.opensOn'), draft.opensOn, setOpensOn, undefined, lockStructure)}
          {day('closesOn', t('cycleForm.closesOn'), draft.closesOn, (v) => set({ closesOn: v }), draft.opensOn)}
        </div>
        <p className="text-xs text-gray-500">{t('cycleForm.windowHint')}</p>
      </fieldset>
    </div>
  )
}
